<template>
  <img class="empty-state-img" :src="emptyStateImageSrc" :alt="alt" />
</template>

<script setup lang="ts">
import emptyStateSrcChartDark from "@shared/assets/images/empty-states/empty-state-chart-dark.svg";
import emptyStateSrcChartLight from "@shared/assets/images/empty-states/empty-state-chart-light.svg";
import emptyStateSrcGlobeDark from "@shared/assets/images/empty-states/empty-state-globe-dark.svg";
import emptyStateSrcGlobeLight from "@shared/assets/images/empty-states/empty-state-globe-light.svg";
import emptyStateSrcHourglass from "@shared/assets/images/empty-states/empty-state-hourglass.svg";
import emptyStateSrcScansDark from "@shared/assets/images/empty-states/empty-state-scans-dark.svg";
import emptyStateSrcScansLight from "@shared/assets/images/empty-states/empty-state-scans-light.svg";
import emptyStateSrcMapDark from "@shared/assets/images/empty-states/empty-state-map-dark.svg";
import emptyStateSrcMapLight from "@shared/assets/images/empty-states/empty-state-map-light.svg";
import emptyStateSrcSwitchDark from "@shared/assets/images/empty-states/empty-state-switch-dark.svg";
import emptyStateSrcSwitchLight from "@shared/assets/images/empty-states/empty-state-switch-light.svg";
import emptyStateSrcMaintenanceDark from "@shared/assets/images/empty-states/empty-state-maintenance-dark.svg";
import emptyStateSrcMaintenanceLight from "@shared/assets/images/empty-states/empty-state-maintenance-light.svg";
import emptyStateSrcNoConnection from "@shared/assets/images/empty-states/empty-state-no-connection.svg";
import emptyStateSrcNotOnMobile from "@shared/assets/images/empty-states/empty-state-not-on-mobile.svg";
import emptyStateSrcSsoDisabledDark from "@shared/assets/images/empty-states/platorm-sso-disabled-page-dark.svg";
import emptyStateSrcSsoDisabledLight from "@shared/assets/images/empty-states/platorm-sso-disabled-page-light.svg";
import emptyStateSrcMagnifyingDark from "@shared/assets/images/empty-states/empty-state-magnifying-dark.svg";
import emptyStateSrcMagnifyingLight from "@shared/assets/images/empty-states/empty-state-magnifying-light.svg";
import emptyStateSrcMlDark from "@shared/assets/images/empty-states/empty-state-ml-dark.svg";
import emptyStateSrcMlLight from "@shared/assets/images/empty-states/empty-state-ml-light.svg";
import emptyStateSettingsDark from "@shared/assets/images/empty-states/empty-state-settings-dark.svg";
import emptyStateSettingsLight from "@shared/assets/images/empty-states/empty-state-settings-light.svg";
import emptyStatePurchaseSuccessDark from "@shared/assets/images/empty-states/empty-state-purchase-success-dark.svg";
import emptyStatePurchaseSuccessLight from "@shared/assets/images/empty-states/empty-state-purchase-success-light.svg";
import emptyStatePeriscope from "@shared/assets/images/empty-states/empty-state-periscope.svg";

import { computed } from "vue";
import { EmptyStateName } from "@shared/types/localTypes/empty-states.ts";
import { useThemeStore } from "@shared/stores/theme.ts";

interface EmptyStateImgProps {
  imageHeight?: number;
  imageHeightUnit?: "px" | "%";
  emptyStateName: EmptyStateName;
  alt?: string;
}

const themeStore = useThemeStore();
const props = withDefaults(defineProps<EmptyStateImgProps>(), {
  alt: "No data available",
});

const emptyStateImageSrc = computed(() =>
  themeStore.isDark ? darkImageSrc.value : lightImageSrc.value,
);

const darkImageSrc = computed(() => {
  switch (props.emptyStateName) {
    case EmptyStateName.CHART:
      return emptyStateSrcChartDark;
    case EmptyStateName.GLOBE:
      return emptyStateSrcGlobeDark;
    case EmptyStateName.HOURGLASS:
      return emptyStateSrcHourglass;
    case EmptyStateName.SCANS:
      return emptyStateSrcScansDark;
    case EmptyStateName.MAP:
      return emptyStateSrcMapDark;
    case EmptyStateName.SWITCH:
      return emptyStateSrcSwitchDark;
    case EmptyStateName.MAINTENANCE:
      return emptyStateSrcMaintenanceDark;
    case EmptyStateName.NO_CONNECTION:
      return emptyStateSrcNoConnection;
    case EmptyStateName.NOT_ON_MOBILE:
      return emptyStateSrcNotOnMobile;
    case EmptyStateName.SSO_DISABLED:
      return emptyStateSrcSsoDisabledDark;
    case EmptyStateName.ML:
      return emptyStateSrcMlDark;
    case EmptyStateName.SETTINGS_WHEEL:
      return emptyStateSettingsDark;
    case EmptyStateName.SUCCESS_CUP:
      return emptyStatePurchaseSuccessDark;
    case EmptyStateName.PERISCOPE:
      return emptyStatePeriscope;
    case EmptyStateName.MAGNIFYING:
    default:
      return emptyStateSrcMagnifyingDark;
  }
});

const lightImageSrc = computed(() => {
  switch (props.emptyStateName) {
    case EmptyStateName.SSO_DISABLED:
      return emptyStateSrcSsoDisabledLight;
    case EmptyStateName.MAINTENANCE:
      return emptyStateSrcMaintenanceLight;
    case EmptyStateName.CHART:
      return emptyStateSrcChartLight;
    case EmptyStateName.GLOBE:
      return emptyStateSrcGlobeLight;
    case EmptyStateName.SCANS:
      return emptyStateSrcScansLight;
    case EmptyStateName.MAP:
      return emptyStateSrcMapLight;
    case EmptyStateName.SWITCH:
      return emptyStateSrcSwitchLight;
    case EmptyStateName.ML:
      return emptyStateSrcMlLight;
    case EmptyStateName.SETTINGS_WHEEL:
      return emptyStateSettingsLight;
    case EmptyStateName.SUCCESS_CUP:
      return emptyStatePurchaseSuccessLight;
    case EmptyStateName.PERISCOPE:
      return emptyStatePeriscope;
    case EmptyStateName.MAGNIFYING:
      return emptyStateSrcMagnifyingLight;
    default:
      return darkImageSrc.value;
  }
});

const heightCssProp = computed(
  () => `${props.imageHeight ?? 130}${props.imageHeightUnit ?? "px"}`,
);
</script>

<style lang="scss" scoped>
@use "@shared/assets/styles/abstracts/functions" as f;
.empty-state-img {
  height: v-bind(heightCssProp);
  width: auto;
}
</style>
